import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { Sparkles, User } from 'lucide-react';
import type { AIChatMessage } from '@/hooks/useAIChat';

interface AIChatMessagesProps {
  messages: AIChatMessage[];
  isStreaming: boolean;
  streamingContent: string;
}

export function AIChatMessages({ messages, isStreaming, streamingContent }: AIChatMessagesProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent]);

  if (messages.length === 0 && !isStreaming) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="text-center max-w-xs">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-brand-primary/20 flex items-center justify-center">
            <Sparkles className="w-8 h-8 text-brand-primary" />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">
            Hi! I'm your AI dating coach
          </h3>
          <p className="text-sm text-brand-muted">
            Ask me about this profile, get conversation starters, or request compatibility insights.
          </p>
          <div className="mt-4 space-y-2">
            <SuggestionChip text="What should I know about them?" />
            <SuggestionChip text="Suggest a conversation starter" />
            <SuggestionChip text="What do their photos say?" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
      {messages.map((message) => (
        <MessageBubble key={message.id} message={message} />
      ))}

      {/* Streaming message */}
      {isStreaming && streamingContent && (
        <div className="flex gap-2 items-start">
          <div className="w-7 h-7 rounded-full bg-brand-primary/20 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-3.5 h-3.5 text-brand-primary" />
          </div>
          <div className="flex-1 bg-brand-surface border border-brand-border rounded-2xl rounded-tl-md px-4 py-2">
            <p className="text-sm text-white whitespace-pre-wrap">
              {streamingContent}
              <span className="inline-block w-1.5 h-4 ml-0.5 bg-brand-primary animate-pulse" />
            </p>
          </div>
        </div>
      )}

      {/* Loading indicator when streaming starts */}
      {isStreaming && !streamingContent && (
        <div className="flex gap-2 items-start">
          <div className="w-7 h-7 rounded-full bg-brand-primary/20 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-3.5 h-3.5 text-brand-primary" />
          </div>
          <div className="bg-brand-surface border border-brand-border rounded-2xl rounded-tl-md px-4 py-3">
            <div className="flex gap-1">
              <span className="w-2 h-2 bg-brand-muted rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-2 h-2 bg-brand-muted rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-2 h-2 bg-brand-muted rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  );
}

function MessageBubble({ message }: { message: AIChatMessage }) {
  const isUser = message.role === 'user';

  return (
    <div className={cn('flex gap-2 items-start', isUser && 'flex-row-reverse')}>
      {/* Avatar */}
      <div
        className={cn(
          'w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0',
          isUser ? 'bg-brand-primary' : 'bg-brand-primary/20'
        )}
      >
        {isUser ? (
          <User className="w-3.5 h-3.5 text-white" />
        ) : (
          <Sparkles className="w-3.5 h-3.5 text-brand-primary" />
        )}
      </div>

      {/* Message */}
      <div
        className={cn(
          'flex-1 max-w-[85%] rounded-2xl px-4 py-2',
          isUser
            ? 'bg-brand-primary text-white rounded-tr-md ml-auto'
            : 'bg-brand-surface border border-brand-border text-white rounded-tl-md'
        )}
      >
        <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
      </div>
    </div>
  );
}

function SuggestionChip({ text }: { text: string }) {
  return (
    <button className="block w-full text-left px-3 py-2 text-xs text-brand-muted bg-brand-surface border border-brand-border rounded-lg hover:border-brand-primary hover:text-white transition-colors">
      {text}
    </button>
  );
}
